/**
 * Subscription Utilities for Feature Access Control
 * Provides helper functions to check subscription tiers and enforce feature limits
 *
 * PAYG (Pay-As-You-Go) replaces previous 'free' and 'standard' tiers.
 */

import React from "react";
import { subscriptionService } from "@/services/subscriptionService";
import { useSupabaseAuth } from "@/context/SupabaseAuthContext";

export type SubscriptionTier = "PAYG" | "professional";

// Report limits by tier (per month)
export const REPORT_LIMITS: Record<SubscriptionTier, number | null> = {
  PAYG: 20,
  professional: 20,
};

// Project limits by tier
export const PROJECT_LIMITS: Record<SubscriptionTier, number | null> = {
  // All users now have a maximum of 10 projects
  PAYG: 10,
  professional: 10,
};

// Bookmark limits by tier
export const BOOKMARK_LIMITS: Record<SubscriptionTier, number | null> = {
  PAYG: 10,
  professional: 10,
};

// Feature access definitions
export interface FeatureAccess {
  aiInsights: boolean;
  whatsappTracking: boolean;
  aiSummary: boolean;
  unlimitedReports: boolean;
  reportLimit: number | null;
  unlimitedProjects: boolean;
  projectLimit: number | null;
  bookmarkLimit: number | null;
}

/**
 * Get user subscription tier from plan_id
 */
export const getSubscriptionTierFromPlanId = (
  planId: string,
): SubscriptionTier => {
  if (!planId) return "PAYG";
  const normalized = planId.toLowerCase();

  switch (normalized) {
    case "standard":
    case "free":
    case "payg":
      return "PAYG";
    case "professional":
      return "professional";
    default:
      // Unknown plan IDs should default to PAYG to avoid accidental privilege escalation
      return "PAYG";
  }
};

/**
 * Get feature access based on subscription tier
 */
export const getFeatureAccess = (tier: SubscriptionTier): FeatureAccess => {
  switch (tier) {
    case "professional":
      return {
        aiInsights: true,
        whatsappTracking: true,
        aiSummary: true,
        unlimitedReports: false,
        reportLimit: REPORT_LIMITS.professional,
        unlimitedProjects: false,
        projectLimit: PROJECT_LIMITS.professional,
        bookmarkLimit: BOOKMARK_LIMITS.professional,
      };
    case "PAYG":
    default:
      return {
        aiInsights: true,
        whatsappTracking: true,
        aiSummary: true,
        unlimitedReports: false,
        reportLimit: REPORT_LIMITS.PAYG,
        unlimitedProjects: false,
        projectLimit: PROJECT_LIMITS.PAYG,
        bookmarkLimit: BOOKMARK_LIMITS.PAYG,
      };
  }
};

/**
 * Check if user has access to a specific feature
 */
export const hasFeatureAccess = (
  tier: SubscriptionTier,
  feature: keyof FeatureAccess,
): boolean => {
  const access = getFeatureAccess(tier);
  const featureValue = access[feature];

  // For boolean features, return the boolean value
  if (typeof featureValue === "boolean") {
    return featureValue;
  }

  // For number/null features (like reportLimit), check if it's not restricted
  return featureValue !== 0;
};

/**
 * Get user's current subscription tier
 */
export const getUserSubscriptionTier = async (
  userId: string,
): Promise<SubscriptionTier> => {
  try {
    // First check if user has an active subscription
    const subscription =
      await subscriptionService.getUserActiveSubscription(userId);

    if (subscription && subscription.status === "active") {
      return getSubscriptionTierFromPlanId(subscription.plan_id);
    }

    // Fallback to account tier if no active subscription
    const accountTier = await subscriptionService.getUserAccountTier(userId);

    if (accountTier === "paid") {
      // If account tier is paid but no active subscription, assume PAYG
      return "PAYG";
    }

    return "PAYG";
  } catch (error) {
    console.error("Error getting user subscription tier:", error);
    return "PAYG"; // Default to PAYG on error
  }
};

/**
 * Generate feature restriction message
 */
export const getFeatureRestrictionMessage = (
  feature: keyof FeatureAccess,
  requiredTier: SubscriptionTier,
): string => {
  const tierNames = {
    PAYG: "PAYG",
    professional: "Professional",
  } as Record<SubscriptionTier, string>;

  const featureNames = {
    aiInsights: "Regional Insights",
    whatsappTracking: "WhatsApp Carbon Tracking",
    aiSummary: "AI Summary",
    unlimitedReports: "Unlimited Reports",
    reportLimit: "Extended Report Limits",
  } as Record<keyof FeatureAccess, string>;

  const featureName = featureNames[feature] || feature;
  const tierName = tierNames[requiredTier] || requiredTier;

  return `${featureName} is available for ${tierName} plan subscribers. Upgrade your plan to access this feature.`;
};

/**
 * Hook to get current user's subscription information
 */
export const useUserSubscription = () => {
  const { user } = useSupabaseAuth();
  const [tier, setTier] = React.useState<SubscriptionTier>("PAYG");
  const [loading, setLoading] = React.useState(true);
  const [featureAccess, setFeatureAccess] = React.useState<FeatureAccess>(
    getFeatureAccess("PAYG"),
  );

  React.useEffect(() => {
    const fetchSubscriptionInfo = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      try {
        const userTier = await getUserSubscriptionTier(user.id);
        setTier(userTier);
        setFeatureAccess(getFeatureAccess(userTier));
      } catch (error) {
        console.error("Error fetching subscription info:", error);
        setTier("PAYG");
        setFeatureAccess(getFeatureAccess("PAYG"));
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionInfo();
  }, [user?.id]);

  const hasAccess = React.useCallback(
    (feature: keyof FeatureAccess) => {
      return hasFeatureAccess(tier, feature);
    },
    [tier],
  );

  const getRestrictionMessage = React.useCallback(
    (feature: keyof FeatureAccess, requiredTier: SubscriptionTier = "PAYG") => {
      return getFeatureRestrictionMessage(feature, requiredTier);
    },
    [],
  );

  return {
    tier,
    featureAccess,
    loading,
    hasAccess,
    getRestrictionMessage,
  };
};
